import { useState } from "react"

import { type UploadKind, uploadMaxBytes } from "@forento/core/models/upload"
import { getExtensionByFileName } from "@forento/core/utilities/file"

import { useToast } from "~/contexts/ToastContext"
import { isConnectionError } from "~/utilities/connectivity"
import { query } from "~/utilities/trpc"

type UploadArgs = {
	file: File
	fileExtension?: string
	kind: UploadKind
}
type UploadResult = { fileName: string; publicUrl: string }
const imageContentTypes: Record<string, string> = {
	gif: "image/gif",
	jpeg: "image/jpeg",
	jpg: "image/jpeg",
	png: "image/png",
	webp: "image/webp",
}
const videoContentTypes: Record<string, string> = {
	m4v: "video/x-m4v",
	mov: "video/quicktime",
	mp4: "video/mp4",
	ogv: "video/ogg",
	webm: "video/webm",
}
const contentTypesByKind: Partial<Record<UploadKind, Record<string, string>>> = {
	"landing-image": imageContentTypes,
	"landing-video": videoContentTypes,
}
export default function useFileUpload() {
	const toast = useToast()
	const prepare = query.upload.prepare.useMutation()
	const [uploadCount, setUploadCount] = useState(0)

	/** Resolves to `null` when the file is rejected before uploading; the user has already been told why. */
	async function upload({ file, fileExtension, kind }: UploadArgs): Promise<UploadResult | null> {
		if (file.size > uploadMaxBytes[kind]) {
			toast.showError(`This file is too large. The maximum size is ${uploadMaxBytes[kind] / (1024 * 1024)} MB.`)
			return null
		}
		setUploadCount(count => count + 1)
		try {
			const resolvedFileExtension = fileExtension ?? getExtensionByFileName(file.name) ?? undefined
			const fallbackContentType =
				resolvedFileExtension !== undefined
					? contentTypesByKind[kind]?.[resolvedFileExtension.toLowerCase()]
					: undefined
			const fileContentType = file instanceof File ? file.type : ""
			const contentType =
				fileContentType.length > 0 &&
				fileContentType !== "application/octet-stream" &&
				(kind !== "landing-image" || fileContentType.startsWith("image/")) &&
				(kind !== "landing-video" || fileContentType.startsWith("video/"))
					? fileContentType
					: (fallbackContentType ?? "application/octet-stream")
			const byteSize = file.size
			const { uploadUrl, fileName, publicUrl } = await prepare.mutateAsync({
				kind,
				contentType,
				byteSize,
				...(resolvedFileExtension !== undefined ? { fileExtension: resolvedFileExtension } : {}),
			})
			const response = await fetch(uploadUrl, {
				method: "PUT",
				body: file,
				headers: {
					"Content-Type": contentType,
					"Content-Length": String(byteSize),
					"x-amz-acl": "public-read",
				},
			})
			if (!response.ok) {
				return await Promise.reject(new Error(`Direct upload failed (${response.status})`))
			}
			setUploadCount(count => count - 1)
			return { fileName, publicUrl }
		} catch (error) {
			console.error(error)
			if (isConnectionError(error) || error instanceof TypeError) {
				toast.showError("Upload interrupted. Check your connection and try uploading again.")
			}
			setUploadCount(count => count - 1)
			return Promise.reject(error)
		}
	}

	return { upload, isPending: prepare.isPending || uploadCount > 0 }
}
